import React, { useState, useEffect } from 'react'
import { ArrowLeft, ChevronLeft, ChevronRight, SlidersHorizontal, CheckCircle } from 'lucide-react'

interface Offer {
  id: number
  title: string
  description: string
  click_url: string
  image: string
  cta_yes: string
  cta_no: string
  advertiser_name: string
  short_description: string
  short_headline: string
}

interface OffersResponse {
  data: {
    offers: Offer[]
  }
}

type LayoutOption = 'inside_order_complete' | 'above_order_status' | 'below_order_status' | 'sidebar_top' | 'sidebar_bottom' | 'full_width_top'

const ShopifyOrderStatusPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [offers, setOffers] = useState<Offer[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedLayout, setSelectedLayout] = useState<LayoutOption>('above_order_status')
  const [showTooltip, setShowTooltip] = useState(false)

  // Fetch offers from Moments API - Same as Thank You Page
  const fetchOffers = async () => {
    try {
      setLoading(true)
      const response = await fetch('https://api.adspostx.com/native/v2/offers.json?api_key=582c2b53-56d5-49a2-a239-7c36e5b0c789', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          country: 'us'
        })
      })

      if (!response.ok) {
        throw new Error('Failed to fetch offers')
      }

      const data: OffersResponse = await response.json()
      setOffers(data.data.offers.slice(0, 3)) // Limit to 3 offers
      setLoading(false)
    } catch (error) {
      console.error('Error fetching offers:', error)
      // Fallback to demo offers
      setOffers([
        {
          id: 1,
          title: 'FREE 14-Day Trial! Discover Roots With Ancestry',
          description: 'Explore your family history with a 14-day free trial. Ancestry membership grants access to billions of records. Discover your roots today!',
          short_headline: 'FREE 14-Day Trial! Discover Roots With Ancestry',
          short_description: 'Explore your family history with a 14-day free trial. Ancestry membership grants access to billions of records. Discover your roots today!',
          image: 'https://via.placeholder.com/80x80/9333EA/white?text=A',
          click_url: '#',
          advertiser_name: 'Ancestry',
          cta_yes: 'Claim Offer!',
          cta_no: 'No, Thanks'
        }
      ])
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOffers()
  }, [])

  // Get current offer based on pagination
  const currentOffer = offers[currentPage - 1]

  const handleOfferClick = (offer: Offer) => {
    if (offer.click_url && offer.click_url !== '#') {
      window.open(offer.click_url, '_blank')
    }
  }

  // Reusable Offers Panel JSX - Exact copy from Thank You Page
  const renderOfferPanel = () => (
    <div className="mb-8">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
          <h3 className="text-lg font-semibold text-gray-900">Your order is complete</h3>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 mb-3">Here are some offers from brands you love!</p>
          {loading ? (
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                <div className="h-10 bg-gray-200 rounded mt-4"></div>
                <div className="h-9 bg-gray-200 rounded"></div>
              </div>
            </div>
          ) : currentOffer ? (
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between">
                <div className="pr-4">
                  <h4 className="text-base font-semibold text-gray-900 mb-2 leading-tight">{currentOffer.short_headline}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3 max-w-md">
                    {currentOffer.short_description}
                  </p>
                </div>
                <div className="flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-md border border-gray-200 bg-white flex items-center justify-center overflow-hidden">
                  {currentOffer.image ? (
                    <img
                      src={currentOffer.image}
                      alt={currentOffer.advertiser_name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  ) : (
                    <span className="text-gray-500 text-xs font-medium">
                      {currentOffer.advertiser_name?.slice(0, 8) || 'Offer'}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2.5 mt-4">
                <button
                  onClick={() => handleOfferClick(currentOffer)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-3 rounded-md shadow-sm transition-colors"
                >
                  {currentOffer.cta_yes || 'Unlock Offer!'}
                </button>
                <button
                  className="w-full text-blue-600 hover:text-blue-700 bg-white border-2 border-gray-200 text-sm font-medium py-2.5 rounded-md transition-colors"
                >
                  {currentOffer.cta_no || 'No, Thanks'}
                </button>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500">
              No offers available at this time.
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-6 py-3">
          <div className="flex items-center justify-center gap-3 text-gray-700">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
              disabled={currentPage === 1}
              aria-label="Previous offer"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {[1, 2, 3].map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors border ${
                  currentPage === page
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100'
                } ${page > offers.length ? 'opacity-40 cursor-not-allowed' : ''}`}
                disabled={page > offers.length && offers.length > 0}
                aria-label={`Offer ${page}`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(Math.min(Math.max(offers.length, 3), currentPage + 1))}
              className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
              disabled={currentPage >= Math.max(offers.length, 3)}
              aria-label="Next offer"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
            <span>Powered by <span className="font-medium text-gray-700">MomentScience</span></span>
            <a href="https://momentscience.com/end-user-privacy-policy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">Privacy Policy</a>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Shopify Style */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-lg font-semibold text-gray-900">MomentScience Swag Store</h1>
            <div className="flex items-center space-x-6">
              <button className="text-sm text-gray-600 hover:text-gray-900">Shop</button>
              <button className="text-sm text-gray-900 font-medium border-b-2 border-gray-900 pb-5">Orders</button>
              <div className="text-sm text-gray-900 bg-gray-100 rounded px-2 py-1">KA</div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Order Status */}
          <div className="lg:col-span-2">
            {/* Order Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <ArrowLeft className="w-4 h-4" />
                <h2 className="text-2xl font-semibold text-gray-900">Order #1057</h2>
              </div>
              <p className="text-sm text-gray-600 ml-6">Confirmed Oct 28</p>
            </div>

            {/* Full Width Top Layout - Below Order ID, Above Confirmed Card */}
            {selectedLayout === 'full_width_top' && (
              <div className="mb-6">
                {renderOfferPanel()}
              </div>
            )}

            {/* Above Order Status Layout - MomentScience Offers */}
            {selectedLayout === 'above_order_status' && renderOfferPanel()}

            {/* Inside Order Complete Box Layout */}
            {selectedLayout === 'inside_order_complete' && renderOfferPanel()}

            {/* Order Status Card - Confirmed */}
            <div className="bg-white rounded-lg border border-gray-200 mb-6">
              <div className="px-6 py-5">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-gray-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">Confirmed</h3>
                    <p className="text-sm text-gray-600">You'll receive an email when your order is ready.</p>
                    <p className="text-sm text-gray-600 mt-2">Oct 28</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Below Order Status Layout */}
            {selectedLayout === 'below_order_status' && (
              <div className="mb-6">
                {renderOfferPanel()}
              </div>
            )}

            {/* Contact and Billing Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Contact information</h3>
                <div className="text-sm space-y-1">
                  <p className="text-gray-900">John Smith</p>
                  <p className="text-gray-600">john.smith@example.com</p>
                </div>
              </div>

              {/* Billing Address */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Billing address</h3>
                <div className="text-sm space-y-1 text-gray-900">
                  <p>John Smith</p>
                  <p>123 Main Street</p>
                  <p>New York NY 10001</p>
                  <p>United States</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-8">
              {/* Sidebar Top Layout */}
              {selectedLayout === 'sidebar_top' && renderOfferPanel()}

              {/* Product Item */}
              <div className="mb-6">
                <div className="flex items-start space-x-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?w=200&h=200&fit=crop" 
                        alt="MomentScience Mug"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span className="absolute -top-2 -right-2 bg-gray-700 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                      1
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">MomentScience Mug</h4>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600"></span>
                      <span className="text-sm font-medium text-gray-900">$0.00</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Totals */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">$0.00</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-gray-900">Free</span>
                </div>
                <div className="flex justify-between items-baseline border-t border-gray-200 pt-3 mt-3">
                  <span className="text-base font-semibold text-gray-900">Total</span>
                  <div className="text-right">
                    <span className="text-xs text-gray-600 mr-2">USD</span>
                    <span className="text-xl font-semibold text-gray-900">$0.00</span>
                  </div>
                </div>
              </div>

              {/* Sidebar Bottom Layout */}
              {selectedLayout === 'sidebar_bottom' && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  {renderOfferPanel()}
                </div>
              )}

              {/* Buy Again Button */}
              <button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors">
                Buy again
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hover-Triggered Options Panel - Left Side */}
      <div className="fixed left-0 top-1/3 -translate-y-1/2 z-40 group">
        {/* Hover trigger area */}
        <div className="w-8 h-32 bg-blue-600 rounded-r-lg flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
          <SlidersHorizontal className="w-5 h-5 text-white" />
        </div>
        
        {/* Sliding panel - appears on hover */}
        <div className="absolute left-0 top-0 w-72 bg-white border-r border-gray-200 shadow-xl overflow-y-auto max-h-[80vh] opacity-0 -translate-x-full group-hover:opacity-100 group-hover:translate-x-8 transition-all duration-300 ease-out rounded-r-lg">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4" />
                Layout Options
              </h4>
              <div 
                className="relative"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-xs font-bold cursor-help">
                  ?
                </div>
                {showTooltip && (
                  <div className="absolute right-0 top-7 w-48 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg z-50">
                    Click any layout to see how offers appear in different positions
                    <div className="absolute -top-1 right-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-3">Choose Offer Placement</div>
              
              {/* Layout options */}
              <button
                onClick={() => setSelectedLayout('full_width_top')}
                className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                  selectedLayout === 'full_width_top'
                    ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-sm">Full Width Top</div>
                <div className="text-xs text-gray-600 mt-1">Above all content</div>
              </button>

              <button
                onClick={() => setSelectedLayout('above_order_status')}
                className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                  selectedLayout === 'above_order_status'
                    ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-sm">Above Order Status</div>
                <div className="text-xs text-gray-600 mt-1">After order header</div>
              </button>

              <button
                onClick={() => setSelectedLayout('inside_order_complete')}
                className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                  selectedLayout === 'inside_order_complete'
                    ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-sm">Inside Order Complete</div>
                <div className="text-xs text-gray-600 mt-1">In a dedicated box</div>
              </button>

              <button
                onClick={() => setSelectedLayout('below_order_status')}
                className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                  selectedLayout === 'below_order_status'
                    ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-sm">Below Order Status</div>
                <div className="text-xs text-gray-600 mt-1">After confirmed card</div>
              </button>

              <button
                onClick={() => setSelectedLayout('sidebar_top')}
                className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                  selectedLayout === 'sidebar_top'
                    ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-sm">Sidebar Top</div>
                <div className="text-xs text-gray-600 mt-1">Above order summary</div>
              </button>

              <button
                onClick={() => setSelectedLayout('sidebar_bottom')}
                className={`w-full text-left px-3 py-3 rounded-lg border transition-all ${
                  selectedLayout === 'sidebar_bottom'
                    ? 'border-blue-600 bg-blue-50 text-blue-800 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-sm">Sidebar Bottom</div>
                <div className="text-xs text-gray-600 mt-1">Below order totals</div>
              </button>

              <div className="pt-3 mt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500 leading-relaxed">
                  💡 Try different placements to see how offers integrate into your order status page
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ShopifyOrderStatusPage
